import { useMemo } from 'react';
import { format, parse } from 'date-fns';

import { TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';
import { useZCalendarWebApiHandler_getTourConfigAndAvailability } from '@zg-rentals/ts-z-calendar-client-react-query';

import { ZCALENDAR_TOUR_CONFIG_QUERY_TAG } from '../constants/queryTags';
import { convertDateToLocalDate } from '../utils/formatTime';

const DATE_KEY_FORMAT = 'yyyy-MM-dd';

type Options = {
  enabled?: boolean;
};

/**
 * Reads a listing's tour config: whether touring is set up at all, and whether it books tours
 * instantly rather than requiring landlord confirmation. The endpoint returns config alongside
 * availability, so the range is collapsed to a single day — only the config is read.
 *
 * `hasTourConfig` is `undefined` until the answer is known, so callers can tell "no touring" apart
 * from "not loaded yet" and avoid hiding UI on a slow or failed request.
 */
const useListingTourConfig = (listingAlias: string, { enabled = true }: Options = {}) => {
  // Keyed to the calendar day instead of frozen at mount, so every instance — whenever it mounted —
  // converges on the same fromDate/toDate and shares one query key.
  const todayKey = format(new Date(), DATE_KEY_FORMAT);
  const today = useMemo(() => parse(todayKey, DATE_KEY_FORMAT, new Date()), [todayKey]);

  const { data, isError, isLoading, isPending } = useZCalendarWebApiHandler_getTourConfigAndAvailability(
    {
      listingAlias,
      fromDate: convertDateToLocalDate(today),
      toDate: convertDateToLocalDate(today),
    },
    {
      enabled: enabled && Boolean(listingAlias),
      meta: {
        tags: [ZCALENDAR_TOUR_CONFIG_QUERY_TAG],
        context: {
          api: 'getTourConfigAndAvailability',
          details: 'useListingTourConfig - Error fetching listing tour config',
        },
      },
    },
  );

  const hasTourConfig = data ? Boolean(data.tourConfig) : undefined;

  const isInstantBook =
    data?.tourConfig?.tourSchedulingTypes.schedulingTypes[0]?.confirmationType === TourConfirmationType.INSTANT;

  // The timezone the listing's tour calendar operates in; the backend reports one even for listings
  // that never set up touring.
  const zoneId = data?.tourConfig?.zoneId ?? data?.zoneIdWithoutTourConfig;

  return { hasTourConfig, isError, isInstantBook, isLoading, isPending, zoneId };
};

export default useListingTourConfig;
